import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, Smartphone, X, Chrome } from 'lucide-react';

// True when running inside the RecargameYa Android shell — we ARE the native
// app, so any "Install PWA" prompt is redundant and confusing there.
const _isNativeShell = () => {
  if (typeof window !== 'undefined' && window.AndroidPrinter) return true;
  if (typeof navigator !== 'undefined' && /RecargameYaApp\//.test(navigator.userAgent || '')) return true;
  return false;
};

const InstallPWA = ({ variant = 'default', showDismiss = true }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if running as installed PWA (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) {
      // Running as PWA - mark as installed and hide button
      setIsInstalled(true);
      localStorage.setItem('pwa-installed', 'true');
      return;
    }
    
    // NOT running as PWA (in browser)
    // If we previously marked as installed but now in browser = user uninstalled
    // Clear the flag so the install button shows again
    const wasInstalled = localStorage.getItem('pwa-installed') === 'true';
    if (wasInstalled && !isStandalone) {
      // User is in browser but was marked as installed
      // They likely uninstalled, so reset the flag
      localStorage.removeItem('pwa-installed');
    }

    // Check if dismissed recently (only for default/floating variant)
    if (variant === 'default') {
      const dismissedTime = localStorage.getItem('pwa-install-dismissed');
      if (dismissedTime) {
        const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
        if (hoursSinceDismissed < 24) {
          setDismissed(true);
          return;
        }
      }
    }

    // Check for iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // For Android/Desktop - listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app was installed - mark in localStorage
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      localStorage.setItem('pwa-installed', 'true');
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [variant]);

  const handleInstallClick = async () => {
    // If we have the deferred prompt, use it
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        localStorage.setItem('pwa-installed', 'true');
      }
      setDeferredPrompt(null);
      return;
    }

    // Otherwise show manual instructions
    setShowInstructions(true);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if already installed (running in standalone mode)
  if (isInstalled) return null;
  
  // Never render inside the RecargameYa Android shell — we ARE the app.
  if (_isNativeShell()) return null;
  
  // Don't show floating if dismissed
  if (dismissed && variant === 'default') return null;

  // Instructions Modal
  if (showInstructions) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-2xl">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-slate-800">
              {isIOS ? 'Instalar en iPhone/iPad' : 'Instalar RecargameYa'}
            </h3>
            <button onClick={() => setShowInstructions(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {isIOS ? (
            <div className="space-y-4 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">1</span>
                <p>Abre esta página en <strong>Safari</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">2</span>
                <p>Toca el botón <strong>Compartir</strong> (cuadro con flecha hacia arriba)</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">3</span>
                <p>Selecciona <strong>"Añadir a pantalla de inicio"</strong></p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 text-sm text-slate-600">
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <Chrome className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <p className="text-amber-800">Para instalar, usa una ventana <strong>normal</strong> de Chrome (no incógnito)</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">1</span>
                <p>Abre <strong>recargameya.com</strong> en Chrome</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">2</span>
                <p>Haz clic en el ícono de <strong>instalación</strong> en la barra de direcciones (o menú ⋮ → "Instalar app")</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold">3</span>
                <p>Confirma la instalación</p>
              </div>
            </div>
          )}
          
          <Button 
            onClick={() => setShowInstructions(false)} 
            className="w-full mt-6 bg-blue-600 hover:bg-blue-700"
          >
            Entendido
          </Button>
        </div>
      </div>
    );
  }

  // Compact button for navbar - always visible until installed
  if (variant === 'compact') {
    return (
      <Button
        onClick={handleInstallClick}
        size="sm"
        className="bg-green-600 hover:bg-green-700 text-white gap-1.5 text-xs"
        data-testid="install-pwa-btn"
      >
        <Download className="w-3.5 h-3.5" />
        Instalar
      </Button>
    );
  }

  // Banner style for login page - always visible until installed
  if (variant === 'banner') {
    return (
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-3 rounded-lg shadow-lg flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-2 rounded-lg">
            <Smartphone className="w-5 h-5" />
          </div>
          <div>
            <p className="font-semibold text-sm">Instala RecargameYa</p>
            <p className="text-xs text-white/80">Acceso rápido desde tu pantalla</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleInstallClick}
            size="sm"
            className="bg-white text-green-700 hover:bg-green-50 font-semibold"
            data-testid="install-pwa-banner-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Instalar
          </Button>
          {showDismiss && (
            <button onClick={handleDismiss} className="text-white/70 hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Default floating button
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-white rounded-xl shadow-2xl p-4 border border-slate-200 max-w-xs">
        <div className="flex items-start gap-3">
          <div className="bg-green-100 p-2 rounded-lg">
            <Smartphone className="w-6 h-6 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-slate-800">Instalar RecargameYa</p>
            <p className="text-xs text-slate-500 mt-0.5">Accede más rápido desde tu pantalla de inicio</p>
          </div>
          {showDismiss && (
            <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button
          onClick={handleInstallClick}
          className="w-full mt-3 bg-green-600 hover:bg-green-700"
          data-testid="install-pwa-floating-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Instalar App
        </Button>
      </div>
    </div>
  );
};

export default InstallPWA;
